import { useMemo, useState } from 'react';
import {
  HARDWARE_ASSET_KINDS,
  HARDWARE_ASSET_STATUSES,
  HARDWARE_COMPONENT_FAMILIES,
  OBJECTIVE_DATA_MODALITIES,
  type HardwareAsset,
  type HardwareAssetKind,
  type HardwareAssetStatus,
  type HardwareComponentFamily,
  type ObjectiveDataModality,
  type UpsertHardwareAssetInput,
} from '@ddp/shared';
import Modal from '@carbon/react/es/components/Modal/Modal';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import Button from '@carbon/react/es/components/Button';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Add from '@carbon/icons-react/es/Add';
import TrashCan from '@carbon/icons-react/es/TrashCan';

import { useT, type MessageKey } from '../../i18n/index.js';
import { FAMILY_LABEL, KIND_LABEL, MODALITY_LABEL, STATUS_META } from './labels.js';
import { HardwarePhotoGallery } from './HardwarePhotoGallery.js';

/**
 * Fiche d'un matériel (ADR-0029) : création et édition. Elle tient sur la **séparation structurante**
 * — deux familles de champs jamais mélangées : « Calcul & capteur » (ce qui adapte le modèle) et
 * « Airframe » (métadonnées d'inventaire). Les champs sont **pilotés par des descripteurs** (§5) :
 * retravailler la mise en page, ou ajouter un champ, ne touche pas la structure du formulaire.
 */

interface FieldSpec {
  key: string;
  labelKey: MessageKey;
  numeric?: boolean;
  unitKey?: MessageKey;
}

// « Calcul & capteur » — la seule enveloppe qui adapte le modèle (la modalité est rendue à part).
const COMPUTE_FIELDS: FieldSpec[] = [
  { key: 'soc', labelKey: 'fleet.field.soc' },
  { key: 'accelerator', labelKey: 'fleet.field.accelerator' },
  { key: 'tops', labelKey: 'fleet.field.tops', numeric: true, unitKey: 'fleet.unit.tops' },
  { key: 'latencyMs', labelKey: 'fleet.field.latencyMs', numeric: true, unitKey: 'fleet.unit.ms' },
  { key: 'sensorFeed', labelKey: 'fleet.field.sensorFeed' },
  { key: 'inputResolution', labelKey: 'fleet.field.inputResolution' },
  { key: 'fovDeg', labelKey: 'fleet.field.fovDeg', numeric: true, unitKey: 'fleet.unit.deg' },
];

// « Airframe » — métadonnées d'inventaire, jamais injectées dans le modèle.
const AIRFRAME_FIELDS: FieldSpec[] = [
  { key: 'massG', labelKey: 'fleet.field.massG', numeric: true, unitKey: 'fleet.unit.g' },
  {
    key: 'enduranceMin',
    labelKey: 'fleet.field.enduranceMin',
    numeric: true,
    unitKey: 'fleet.unit.min',
  },
  { key: 'propulsion', labelKey: 'fleet.field.propulsion' },
  {
    key: 'maxSpeedMs',
    labelKey: 'fleet.field.maxSpeedMs',
    numeric: true,
    unitKey: 'fleet.unit.ms2',
  },
  { key: 'registration', labelKey: 'fleet.field.registration' },
  { key: 'homeBase', labelKey: 'fleet.field.homeBase' },
];

const NUMERIC_KEYS = new Set<string>(
  [...COMPUTE_FIELDS, ...AIRFRAME_FIELDS].filter((f) => f.numeric).map((f) => f.key),
);

interface ComponentDraft {
  family: HardwareComponentFamily;
  name: string;
  detail: string;
}

interface FormState {
  kind: HardwareAssetKind;
  name: string;
  reference: string;
  status: HardwareAssetStatus;
  modality: ObjectiveDataModality | '';
  /** Toutes les valeurs de spec en texte ; converties au dépôt selon `NUMERIC_KEYS`. */
  values: Record<string, string>;
  components: ComponentDraft[];
}

function toText(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

function initialForm(asset: HardwareAsset | null): FormState {
  const values: Record<string, string> = {};
  for (const f of [...COMPUTE_FIELDS, ...AIRFRAME_FIELDS]) {
    const source = COMPUTE_FIELDS.includes(f) ? asset?.compute : asset?.airframe;
    values[f.key] = toText((source as Record<string, unknown> | undefined)?.[f.key]);
  }
  return {
    kind: asset?.kind ?? 'DRONE',
    name: asset?.name ?? '',
    reference: asset?.reference ?? '',
    status: asset?.status ?? 'STOCK',
    modality: asset?.compute.modality ?? '',
    values,
    components: (asset?.components ?? []).map((c) => ({
      family: c.family,
      name: c.name,
      detail: c.detail ?? '',
    })),
  };
}

/** Reconstruit un bloc de spec (compute/airframe) à partir des champs texte, en typant les numériques. */
function buildBlock(values: Record<string, string>, fields: FieldSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = values[f.key]?.trim();
    if (!raw) continue;
    out[f.key] = NUMERIC_KEYS.has(f.key) ? Number(raw) : raw;
  }
  return out;
}

export function HardwareAssetModal({
  asset,
  onClose,
  onSubmit,
  onDelete,
}: {
  /** `null` = création ; sinon édition du matériel existant. */
  asset: HardwareAsset | null;
  onClose: () => void;
  onSubmit: (input: UpsertHardwareAssetInput) => Promise<void>;
  /** Présent seulement en édition (rôle éditeur) : retire le matériel après confirmation en place. */
  onDelete?: () => Promise<void>;
}) {
  const t = useT();
  const [form, setForm] = useState<FormState>(() => initialForm(asset));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isNew = asset === null;
  const nameInvalid = form.name.trim().length < 1;

  const setValue = (key: string, value: string) =>
    setForm((f) => ({ ...f, values: { ...f.values, [key]: value } }));

  const addComponent = () =>
    setForm((f) => ({
      ...f,
      components: [...f.components, { family: 'COMPUTE', name: '', detail: '' }],
    }));
  const setComponent = (i: number, patch: Partial<ComponentDraft>) =>
    setForm((f) => ({
      ...f,
      components: f.components.map((c, j) => (j === i ? { ...c, ...patch } : c)),
    }));
  const removeComponent = (i: number) =>
    setForm((f) => ({ ...f, components: f.components.filter((_, j) => j !== i) }));

  const compute = useMemo(
    () => ({
      ...buildBlock(form.values, COMPUTE_FIELDS),
      ...(form.modality ? { modality: form.modality } : {}),
    }),
    [form.values, form.modality],
  );

  const submit = async () => {
    if (nameInvalid) return;
    setBusy(true);
    setError(null);
    try {
      const input: UpsertHardwareAssetInput = {
        kind: form.kind,
        name: form.name.trim(),
        reference: form.reference.trim() || undefined,
        status: form.status,
        compute,
        airframe: buildBlock(form.values, AIRFRAME_FIELDS),
        components: form.components
          .filter((c) => c.name.trim().length > 0)
          .map((c) => ({
            family: c.family,
            name: c.name.trim(),
            detail: c.detail.trim() || undefined,
          })),
      };
      await onSubmit(input);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      modalHeading={isNew ? t('fleet.modal.createTitle') : form.name || t('fleet.modal.editTitle')}
      modalLabel={t('fleet.title')}
      primaryButtonText={isNew ? t('fleet.modal.create') : t('fleet.save')}
      secondaryButtonText={t('fleet.cancel')}
      primaryButtonDisabled={busy || nameInvalid}
      onRequestClose={onClose}
      onRequestSubmit={() => void submit()}
      size="lg"
    >
      {error && (
        <InlineNotification
          kind="error"
          title={t('common.error')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      {/* Identité */}
      <div className="ddp-fleet-form__grid">
        <TextInput
          id="hw-name"
          labelText={t('fleet.field.name')}
          value={form.name}
          invalid={nameInvalid}
          invalidText={t('fleet.field.nameInvalid')}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        />
        <TextInput
          id="hw-reference"
          labelText={t('fleet.field.reference')}
          value={form.reference}
          onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
        />
        <Select
          id="hw-kind"
          labelText={t('fleet.field.kind')}
          value={form.kind}
          onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as HardwareAssetKind }))}
        >
          {HARDWARE_ASSET_KINDS.map((k) => (
            <SelectItem key={k} value={k} text={t(KIND_LABEL[k])} />
          ))}
        </Select>
        <Select
          id="hw-status"
          labelText={t('fleet.field.status')}
          value={form.status}
          onChange={(e) =>
            setForm((f) => ({ ...f, status: e.target.value as HardwareAssetStatus }))
          }
        >
          {HARDWARE_ASSET_STATUSES.map((s) => (
            <SelectItem key={s} value={s} text={t(STATUS_META[s].key)} />
          ))}
        </Select>
      </div>

      {/* Famille 1 — adapte le modèle */}
      <section className="ddp-fleet-form__fam ddp-fleet-form__fam--model">
        <header className="ddp-fleet-form__fam-head">
          <span className="ddp-fleet-form__fam-badge">{t('fleet.fam.model.badge')}</span>
          <div>
            <h4 className="ddp-fleet-form__fam-title">{t('fleet.fam.model.title')}</h4>
            <p className="ddp-fleet-form__fam-sub">{t('fleet.fam.model.sub')}</p>
          </div>
        </header>
        <div className="ddp-fleet-form__grid">
          {COMPUTE_FIELDS.map((f) => (
            <SpecField key={f.key} spec={f} value={form.values[f.key] ?? ''} onChange={setValue} />
          ))}
          <Select
            id="hw-modality"
            labelText={t('fleet.field.modality')}
            value={form.modality}
            onChange={(e) =>
              setForm((fs) => ({ ...fs, modality: e.target.value as ObjectiveDataModality | '' }))
            }
          >
            <SelectItem value="" text={t('fleet.field.unset')} />
            {OBJECTIVE_DATA_MODALITIES.map((m) => (
              <SelectItem key={m} value={m} text={t(MODALITY_LABEL[m])} />
            ))}
          </Select>
        </div>
      </section>

      {/* Famille 2 — métadonnées */}
      <section className="ddp-fleet-form__fam ddp-fleet-form__fam--meta">
        <header className="ddp-fleet-form__fam-head">
          <span className="ddp-fleet-form__fam-badge">{t('fleet.fam.meta.badge')}</span>
          <div>
            <h4 className="ddp-fleet-form__fam-title">{t('fleet.fam.meta.title')}</h4>
            <p className="ddp-fleet-form__fam-sub">{t('fleet.fam.meta.sub')}</p>
          </div>
        </header>
        <div className="ddp-fleet-form__grid">
          {AIRFRAME_FIELDS.map((f) => (
            <SpecField key={f.key} spec={f} value={form.values[f.key] ?? ''} onChange={setValue} />
          ))}
        </div>
      </section>

      {/* Composants */}
      <section className="ddp-fleet-form__components">
        <h4 className="ddp-fleet-form__fam-title">{t('fleet.components.title')}</h4>
        {form.components.map((c, i) => (
          <div key={i} className="ddp-fleet-form__component">
            <Select
              id={`hw-comp-fam-${i}`}
              labelText={t('fleet.field.family')}
              value={c.family}
              onChange={(e) =>
                setComponent(i, { family: e.target.value as HardwareComponentFamily })
              }
            >
              {HARDWARE_COMPONENT_FAMILIES.map((fam) => (
                <SelectItem key={fam} value={fam} text={t(FAMILY_LABEL[fam])} />
              ))}
            </Select>
            <TextInput
              id={`hw-comp-name-${i}`}
              labelText={t('fleet.field.componentName')}
              value={c.name}
              onChange={(e) => setComponent(i, { name: e.target.value })}
            />
            <TextInput
              id={`hw-comp-detail-${i}`}
              labelText={t('fleet.field.componentDetail')}
              value={c.detail}
              onChange={(e) => setComponent(i, { detail: e.target.value })}
            />
            <Button
              kind="ghost"
              size="md"
              hasIconOnly
              iconDescription={t('fleet.components.remove')}
              renderIcon={TrashCan}
              onClick={() => removeComponent(i)}
              className="ddp-fleet-form__component-del"
            />
          </div>
        ))}
        <Button kind="tertiary" size="sm" renderIcon={Add} onClick={addComponent}>
          {t('fleet.components.add')}
        </Button>
      </section>

      {/* Branchements — points d'ancrage nommés (ADR-0029 §4) */}
      <p className="ddp-fleet-form__wiring">{t('fleet.wiring.note')}</p>

      {/* Photos du build (ADR-0032) — seulement sur un matériel déjà créé (il faut son id). */}
      {!isNew && asset && <HardwarePhotoGallery assetId={asset.id} />}

      {/* Retrait — confirmation en place, jamais destructif au premier clic. */}
      {onDelete &&
        (confirmDelete ? (
          <div className="ddp-fleet-form__delete">
            <span>{t('fleet.delete.confirm')}</span>
            <Button
              kind="danger"
              size="sm"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await onDelete();
                  onClose();
                } catch (err) {
                  setError(err instanceof Error ? err.message : t('common.error'));
                  setBusy(false);
                }
              }}
            >
              {t('fleet.delete.yes')}
            </Button>
            <Button kind="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
              {t('fleet.cancel')}
            </Button>
          </div>
        ) : (
          <Button
            kind="danger--ghost"
            size="sm"
            renderIcon={TrashCan}
            onClick={() => setConfirmDelete(true)}
            className="ddp-fleet-form__delete-trigger"
          >
            {t('fleet.delete.trigger')}
          </Button>
        ))}
    </Modal>
  );
}

function SpecField({
  spec,
  value,
  onChange,
}: {
  spec: FieldSpec;
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const t = useT();
  const label = spec.unitKey ? `${t(spec.labelKey)} (${t(spec.unitKey)})` : t(spec.labelKey);
  return (
    <TextInput
      id={`hw-${spec.key}`}
      labelText={label}
      value={value}
      type={spec.numeric ? 'number' : 'text'}
      onChange={(e) => onChange(spec.key, e.target.value)}
    />
  );
}
